import React from 'react';

import classNames from 'classnames';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import BootstrapDropdown from 'react-bootstrap/Dropdown';

import { Loader } from '@src/components/ui';

import '../shared/dropdown.scss';

const ALL_OPTION = 'all-options';

/**
 * Dropdown Component
 *
 * A customizable dropdown component with Bootstrap styling that supports
 * loading states, custom options, and callback handling.
 *
 * ## Features
 * - Loading state support with spinner
 * - Customizable option list with types
 * - Optional "All Categories" blank option
 * - Callback-based or navigation-based selection
 * - Accessible ARIA roles and keyboard navigation
 *
 * ## Props
 * - `list` (Array): Array of option objects with id, title, and optional type
 * - `isLoading` (boolean): Shows loading spinner when true
 * - `allowBlank` (boolean): Include "All Categories" option (default: true)
 * - `defaultSelection` (string): Text for blank option (default: "All Categories")
 * - `selectedOption` (string): Currently selected option ID
 * - `onChange` (function): Callback when option is selected
 * - `title` (string): Default display text when no option selected
 *
 * @component
 * @example
 * ```jsx
 * const options = [
 *   { id: '1', title: 'Option 1' },
 *   { id: '2', title: 'Option 2', type: 'special' }
 * ];
 *
 * <Dropdown
 *   title="Select Category"
 *   list={options}
 *   selectedOption="1"
 *   onChange={(id, type) => console.log(id, type)}
 * />
 * ```
 */
const Dropdown = ({
    list = [],
    isLoading,
    allowBlank = true,
    defaultSelection = 'All Categories',
    selectedOption,
    onChange,
    title,
}) => {
    /**
     * Handle option click event
     * @param {Event} event - Click event from Bootstrap dropdown item
     */
    const handleOptionClick = React.useCallback((event) => {
        const targetId = event.nativeEvent.target.id;
        const targetType = event.nativeEvent.target.type;

        if (onChange) {
            onChange(targetId, targetType);
        }
    }, [onChange]);

    /**
     * Get the display label for the currently selected option
     * @returns {string} Display text for the dropdown toggle
     */
    const selectedLabel = React.useMemo(() => {
        if (!selectedOption || selectedOption === ALL_OPTION) {
            return title;
        }

        if (!list || list.length === 0) {
            return title;
        }

        const thisSelectedOption = list.find((o) => o.id === selectedOption);
        return get(thisSelectedOption, 'title', title);
    }, [selectedOption, title, list]);

    /**
     * Render the list of options from the provided list prop
     * @returns {React.ReactElement[]|null} Array of dropdown items or null
     */
    const renderOptions = React.useCallback(() => {
        if (!list || list.length === 0) {
            return null;
        }

        return list.map((item) => (
            <BootstrapDropdown.Item
                key={`li-${item.id}`}
                id={item.id}
                type={item.type}
                eventKey={item.id}
                tabIndex={-1}
                role="menuitem"
                onClick={handleOptionClick}
            >
                {item.title}
            </BootstrapDropdown.Item>
        ));
    }, [list, handleOptionClick]);

    /**
     * Render the dropdown menu content
     * @returns {React.ReactElement} Dropdown menu with options or loader
     */
    const renderOpen = React.useCallback(() => {
        const selectOptionsClass = classNames({
            'select-options': true,
        });

        if (isLoading) {
            return (
                <BootstrapDropdown.Menu className={selectOptionsClass}>
                    <Loader />
                </BootstrapDropdown.Menu>
            );
        }

        return (
            <BootstrapDropdown.Menu
                key={`dropdown-select-ul-${title}`}
                id={`dropdown-select-ul-${title}`}
                className={selectOptionsClass}
                role="menu"
            >
                {
                    allowBlank && (
                        <BootstrapDropdown.Item
                            key={`li-all-options-${title}`}
                            eventKey=""
                            tabIndex={-1}
                            role="menuitem"
                            onClick={handleOptionClick}
                        >
                            {defaultSelection}
                        </BootstrapDropdown.Item>
                    )
                }
                {renderOptions()}
            </BootstrapDropdown.Menu>
        );
    }, [title, allowBlank, defaultSelection, isLoading, handleOptionClick, renderOptions]);

    return (
        <div key={`dropdown-container-${title}`} className="dropdown-container ml-form floating-label">
            <div className="ml-form-field ml-form-field-select">
                <div className="select">
                    <BootstrapDropdown
                        key={`dropdown-label-${title}`}
                        role="menu"
                    >
                        <BootstrapDropdown.Toggle variant="primary" className="select-styled">
                            {selectedLabel}
                        </BootstrapDropdown.Toggle>
                        {renderOpen()}
                    </BootstrapDropdown>
                </div>
            </div>
        </div>
    );
};

Dropdown.propTypes = {
    list: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        title: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.array,
        ]).isRequired,
        type: PropTypes.string,
    })),
    isLoading: PropTypes.bool,
    allowBlank: PropTypes.bool,
    defaultSelection: PropTypes.string,
    selectedOption: PropTypes.string,
    onChange: PropTypes.func,
    title: PropTypes.string,
};


export default React.memo(Dropdown);

